import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useContext, useMemo, useState, MouseEvent, JSX } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Collapse } from 'src/components/ui';
import ReactMarkdown from 'react-markdown';
import StaticTextBlurb from '../customFields/StaticTextBlurb';

import useResourceLoader from '../../util/useResourceLoader';
import UWEEntityApi from '../../api/UWEEntityApi';
import Loader from '../Loader';
import Notification from '../Notification';
import useTitle from '../../util/useTitle';
import Pagination from '../Pagination';
import useSearchParams from '../../util/useSearchParams';
import SearchBar from '../Search/SearchBar';
import { AuthContext } from '../../context/AuthContext';
import Jnx from '../../util/jnx';
import ModalContainer from '../ModalContainer';
import { applyTemplate, TemplateVariables } from '../../util/markdownTemplate';
import { useIsMobile } from '../../hooks/useIsMobile';
import classnames from '../../util/classnames';
import { ActionButtonsCell, FnBindings, parseSearchFormDefinition, WorkTrayAction, WorkTrayElement } from './WorkTray';
import { useOpenModal } from '../FormModal';
import { EmptyState } from '../Icons/EmptyStates';
import useTestId, { getTestId } from '../../hooks/useTestId';
import { useWorkpageTestIdScope } from '../../context/WorkpageTestIdContext';
import Icon from 'src/components/Icon';
import staggerDelay from '../../util/staggerDelay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Filter field definition (mirrors WorkTray filters). */
export interface CardTrayFilter {
  title: string;
  key: string;
  type?: string;
  'ui:field'?: string;
  lookup?: object;
  enum?: string[];
  enumNames?: string[];
  format?: string;
  ignoreIfFalsy?: boolean;
}

/**
 * Element schema for a CardTray workpage element.
 *
 * Example:
 * ```json
 * {
 *   "type": "cardtray",
 *   "title": "Reportes en proceso",
 *   "entityType": "Report",
 *   "columns": 3,
 *   "lookupParams": { "all": "0" },
 *   "transforms": {
 *     "status.0.name": "$value = 'Create Report' ? 'Comenzado' : $value = 'Data Modifications' ? 'Rechazado' : $value = 'Report Revision' ? 'En Revisión' : $value"
 *   },
 *   "markdownTemplate": "### ${metadata.reportingWindowPeriod}\n**Estado:** ${status.0.name}\n**Fecha Comenzado:** ${createdAt|date:ll}",
 *   "actions": [{ "label": "Continuar", "route": "/workflow/report-submission/$id", "color": "primary" }],
 *   "filters": [{ "title": "Año", "key": "metadata-like-year", "type": "string" }]
 * }
 * ```
 */
export interface CardTrayElement {
  /** Optional stable identifier for testids; falls back to widget type. */
  name?: string;
  title: string;
  description?: string;
  ':classNames'?: string;
  tableActions: WorkTrayAction[];
  scope?: Scope;
  /** UWE entity type name (e.g. "Report"). */
  entityType?: string;
  /** Fallback API resource path when entityType is not set. */
  resource?: string;
  /** Additional query params forwarded to getWorkTray. */
  lookupParams?: Record<string, string | string[]>;
  /** Default sort key forwarded to getWorkTray. */
  defaultFilter?: string;
  ignoreTitle?: boolean;
  /**
   * Markdown string with `${field}` and `${field|formatter:arg}` placeholders
   * resolved against each (optionally transformed) row object via `applyTemplate`.
   */
  markdownTemplate?: string;
  /**
   * HTML string with `${field}` and `${field|formatter:arg}` placeholders
   * resolved against each (optionally transformed) row object via `applyTemplate`.
   * Rendered using StaticTextBlurb (dangerouslySetInnerHTML).
   * Use this when you need full HTML control over the card content.
   * Takes precedence over `markdownTemplate` if both are provided.
   */
  htmlTemplate?: string;
  /**
   * Field-level JNX value transforms applied to the row object before templating.
   * Keys are dot/bracket field paths matching those used in `markdownTemplate`.
   * Each value is a JNX expression with `$value` bound to the raw field value.
   *
   * The transforms produce a shallow-merged clone of the row object, so the
   * original template syntax (`${status.0.name}`) still works — no new path
   * syntax is needed.
   *
   * @example
   * ```json
   * "transforms": {
   *   "status.0.name": "$value = 'Create Report' ? 'Comenzado' : $value"
   * }
   * ```
   */
  transforms?: Record<string, string>;
  actions?: WorkTrayAction[];
  filters?: CardTrayFilter[];
  /**
   * Maximum number of cards per row at the widest breakpoint.
   * Responsive breakpoints will always override this downward.
   * Accepts 1 | 2 | 3 | 4. Defaults to 3.
   */
  columns?: 1 | 2 | 3 | 4;
  /**
   * When explicitly `true`, each item is wrapped in a Bootstrap `<Card>`.
   * Defaults to `false` — the flat item style is the default.
   */
  'ui:asCard'?: boolean;
  'ui:tableActionsPosition'?: string;
  emptyStateIcon?: string;
  emptyStateMessage?: string;
}

/** Generic row object returned by the API. */
type RowObject = Record<string, unknown>;

/** Scope object threaded through the workpage tree. */
type Scope = Record<string, unknown>;

/** Shape of the paged response from UWEEntityApi.getWorkTray. */
interface PagedResponse {
  from: number;
  to: number;
  count: number;
  items: RowObject[];
}

interface CardTrayProps {
  element: CardTrayElement;
  scope?: Scope;
  inForm?: boolean; //whether this is the main page component or part of another page
  /** Identifies this element within its Workpage — namespaces its pagination/sort query params (see useSearchParams) so sibling elements don't collide. */
  elementId?: string;
}

// ---------------------------------------------------------------------------
// CardTray (main component)
// ---------------------------------------------------------------------------

/**
 * CardTray
 *
 * Renders a paginated, filterable grid of cards driven by a workpage element
 * schema. Each card body is produced by interpolating `markdownTemplate` with
 * the row object, followed by a row of action buttons.
 */
function CardTray({ element, scope: propScope, inForm = false, elementId }: CardTrayProps): JSX.Element {
  const workpageScope = useWorkpageTestIdScope();
  const cardtrayBase = useMemo(() => [workpageScope, element.name || 'cardtray'].filter(Boolean).join('.'), [workpageScope, element.name]);
  const filterBtnTestId = useTestId(cardtrayBase, '.', 'filter-toggle');
  const [lastReload, setLastReload] = useState<number | undefined>();

  const scope = useMemo<Scope>(
    () => ({
      ...propScope,
      ...element?.scope,
      lastReload,
      reload: () => setLastReload(new Date().getTime()),
    }),
    [propScope, lastReload, element]
  );

  useTitle(inForm || element.ignoreTitle ? '' : element.title || '');

  const auth = useContext(AuthContext);
  const actualOrganizationalUnitId: string | undefined = (auth as any)?.user?.user?.actualOrganizationalUnitId;

  const [formDefinition, sFilters] = parseSearchFormDefinition(element as WorkTrayElement);
  const [filters, setFilters] = useState<Record<string, string>>({ ...sFilters });
  const [showFilter, setShowFilter] = useState(false);
  const isMobile = useIsMobile();

  const toggleFilter = (e: MouseEvent) => {
    e.preventDefault();
    setShowFilter(prev => !prev);
  };

  const [[offset, setOffset], [size, setSize], [sortBy]] = useSearchParams(
    {
      offset: 0,
      size: 10,
      sortBy: element.defaultFilter,
    },
    elementId
  );

  const lookup = useMemo(() => {
    const entityType = element.entityType;
    return {
      entity: entityType,
      api: !entityType && element.resource ? `api:${element.resource}` : null,
      params: element.lookupParams,
    };
  }, [element]);

  const [paging, loading = true, error] = useResourceLoader<PagedResponse | null>(
    async abortController => {
      const entity = Array.isArray(lookup.entity) ? lookup.entity.join(',') : lookup.entity;

      let orgUnitId = actualOrganizationalUnitId;
      if (!orgUnitId) {
        const roles: any[] = (auth as any)?.user?.organizationalUnitRoles ?? [];
        if (roles.length > 0) orgUnitId = roles[0].organizationalUnitId;
      }

      const resource = lookup.entity ? `uwe-entities/${encodeURIComponent(entity!)}/list/${orgUnitId}` : lookup.api?.split(':')[1];

      if (!resource) return null;

      const mergedFilters: Record<string, string> = { ...filters };
      if (lookup.params) {
        Object.entries(lookup.params).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            mergedFilters[key] = (value as string[]).filter(Boolean).join(',');
          } else if (value != null && typeof value !== 'object') {
            mergedFilters[key] = String(value);
          }
        });
      }

      element.filters?.forEach(f => {
        if (f.ignoreIfFalsy) {
          const v = mergedFilters[f.key];
          if (!v || v === 'false' || v === '0') delete mergedFilters[f.key];
        }
      });
      const data = await UWEEntityApi.getWorkTray({ resource, offset, size, sortBy, ...mergedFilters, abortController });

      return Array.isArray(data) ? { from: 0, to: data.length, count: data.length, items: data } : data;
    },
    [size, offset, sortBy, filters, element, lookup, lastReload, actualOrganizationalUnitId, auth]
  );

  const { from = 0, count = 0, items: data = [] } = paging ?? {};

  const onSearch = (update?: Record<string, string>) => {
    setFilters(prev => ({ ...prev, ...update }));
    setOffset(undefined);
    if (isMobile) setShowFilter(false);
  };

  const onClearSearch = () => {
    setFilters({});
    setOffset(undefined);
  };

  const [confirmError, setConfirmError] = useState<unknown>();
  const openModal = useOpenModal();

  const fnBindings: FnBindings = {
    onSearch,
    openModal,
    setConfirmError,
  };

  const { markdownTemplate = '', htmlTemplate, actions = [], columns = 3, transforms } = element;
  const asCard = element['ui:asCard'] === true;

  const filtersApplied = !!(element.filters?.length && Object.values(filters).some(Boolean));

  // ── Shared: the scrollable grid content ───────────────────────────────────
  // `full-height` while loading tells Workpage's shrink-to-content sizing to hold this widget at its
  // fair share: the loader is absolutely positioned, so measuring it would collapse the row to just
  // the header and then jump once the cards arrive.
  const gridContent = (
    <div className={classnames('cardtray-scroll', { 'full-height': loading })}>
      {confirmError ? (
        <div className="cardtray-notification">
          <Notification error={confirmError} onClose={() => setConfirmError(undefined)} />
        </div>
      ) : null}
      {loading ? (
        <div className="cardtray-loader">
          <Loader>
            <Trans>Loading {element.title}</Trans>
          </Loader>
        </div>
      ) : error ? (
        <div className="cardtray-notification">
          <Notification error={error} />
        </div>
      ) : data.length > 0 ? (
        <div className={`cardtray-grid cardtray-grid--cols-${columns}`}>
          {data.map((rowObject, idx) => (
            <CardTrayItem
              key={idx}
              rowObject={rowObject}
              markdownTemplate={markdownTemplate}
              htmlTemplate={htmlTemplate}
              transforms={transforms}
              actions={actions}
              scope={scope}
              fnBindings={fnBindings}
              testIdBase={cardtrayBase}
              rowIndex={idx}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={element.emptyStateIcon}
          message={element.emptyStateMessage ?? t`The list of ${element.title} is empty.`}
          size={150}
          filtersApplied={filtersApplied}
          clearFilters={filtersApplied ? onClearSearch : null}
        />
      )}
    </div>
  );

  const pagination =
    paging && paging?.count > paging?.items?.length ? (
      <Pagination offset={from} count={count} size={size} setSize={setSize} setOffset={setOffset} />
    ) : null;

  const filterButton =
    element.filters && element.filters.length > 0 ? (
      <Button onClick={toggleFilter} type="button" className="filter-button p-0" outline color="secondary" active={showFilter} {...filterBtnTestId}>
        <Icon icon="fa fa-filter" />
      </Button>
    ) : null;

  const tableActions =
    element.tableActions && element.tableActions.length > 0 ? (
      <ActionButtonsCell actions={element.tableActions} scope={scope} fnBindings={fnBindings} testIdBase={cardtrayBase} size="xs" />
    ) : null;

  const filterCollapse = formDefinition ? (
    <Collapse isOpen={showFilter} className="worktray-filters-collapse">
      <SearchBar formDefinition={formDefinition} filters={filters} onSearch={onSearch} onClearSearch={onClearSearch} />
    </Collapse>
  ) : null;

  let tableActionsOnRight = false;
  if (element['ui:tableActionsPosition'] === 'right') {
    tableActionsOnRight = true;
  }

  // ── Card shell layout (ui:asCard === true) ────────────────────────────────
  if (asCard) {
    return (
      <div className={classnames('cardtray cardtray--as-card', element[':classNames'])}>
        <ModalContainer />
        <Card className="animated-show inbox-card primary no-bottom-gap">
          <CardHeader>
            <div className="cardtray-header">
              <div className="cardtray-header-left">
                <div className="cardtray-header-title">
                  <span>{element.title}</span>
                  {element.description && (
                    <div className="worktray-description">
                      <small>{element.description}</small>
                    </div>
                  )}
                </div>
                {tableActionsOnRight && filterButton}
                {!tableActionsOnRight && tableActions}
              </div>
              {!tableActionsOnRight && filterButton}
              {tableActionsOnRight && tableActions}
            </div>
          </CardHeader>
          <div style={{ position: 'relative' }}>{filterCollapse}</div>
        </Card>
        <Card className={classnames('animated-show inbox-card', { 'square-top': !showFilter })}>
          <CardBody className="cardtray-card-body">{gridContent}</CardBody>
          <CardFooter className="pagination">{pagination}</CardFooter>
        </Card>
      </div>
    );
  }

  // ── Flat layout (default) ─────────────────────────────────────────────────
  return (
    <div className={classnames('cardtray', element[':classNames'])}>
      <ModalContainer />

      {/* Header row: title left, filter button right */}
      <div className="cardtray-header">
        <div className="cardtray-header-left">
          <div className="cardtray-header-title">
            <h5 className="mb-0 nowrap">{element.title}</h5>
            {element.description && <small className="text-muted">{element.description}</small>}
          </div>
          {tableActionsOnRight && <small className="ml-2">{filterButton}</small>}
          {!tableActionsOnRight && tableActions}
        </div>
        {!tableActionsOnRight && filterButton}
        {tableActionsOnRight && tableActions}
      </div>

      {isMobile ? filterCollapse : <div style={{ position: 'relative' }}>{filterCollapse}</div>}

      {gridContent}

      {/* Pagination always at bottom */}
      <div className="cardtray-pagination">{pagination}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardTrayItem
// ---------------------------------------------------------------------------

/**
 * Apply JNX-based value transforms to a deep clone of the row object.
 *
 * Key syntax (two forms):
 *
 *   "status.0.name"
 *     Read $value from `status.0.name`, evaluate expr, write result back to
 *     the same path.
 *
 *   "status.0.badgeClass|status.0.name"
 *     Read $value from `status.0.name` (after the `|`), evaluate expr, write
 *     result to `status.0.badgeClass` (before the `|`).
 *     If the write-path doesn't exist yet in the clone, it is created by
 *     merging into the nearest existing ancestor object.
 *
 * Transforms are applied in object-key order, so declare derived/virtual keys
 * before overwriting the source field they depend on.
 */
export function applyTransforms(rowObject: RowObject, transforms: Record<string, string>): RowObject {
  if (!transforms || Object.keys(transforms).length === 0) return rowObject;

  const clone: RowObject = JSON.parse(JSON.stringify(rowObject));

  /** Walk a dot-path on any object and return the leaf value. */
  function readPath(root: unknown, path: string): string {
    const segs = path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean);
    let cur: unknown = root;
    for (const seg of segs) {
      if (cur === null || cur === undefined) return '';
      if (Array.isArray(cur)) cur = (cur as unknown[])[Number(seg)];
      else if (typeof cur === 'object') cur = (cur as Record<string, unknown>)[seg];
      else return '';
    }
    return cur === null || cur === undefined ? '' : String(cur);
  }

  /** Walk a dot-path and write `value` at the leaf, creating intermediate
   *  plain objects as needed. */
  function writePath(root: Record<string, unknown>, path: string, value: string): void {
    const segs = path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .filter(Boolean);
    let target: Record<string, unknown> = root;

    for (let i = 0; i < segs.length - 1; i++) {
      const seg = segs[i];
      const next = Array.isArray(target) ? (target as unknown[])[Number(seg)] : target[seg];

      if (next !== null && typeof next === 'object') {
        target = next as Record<string, unknown>;
      } else {
        // Create a plain object at this segment if missing
        const newObj: Record<string, unknown> = {};
        if (Array.isArray(target)) {
          (target as unknown[])[Number(seg)] = newObj;
        } else {
          target[seg] = newObj;
        }
        target = newObj;
      }
    }

    const lastSeg = segs[segs.length - 1];
    if (Array.isArray(target)) {
      (target as unknown[])[Number(lastSeg)] = value;
    } else {
      target[lastSeg] = value;
    }
  }

  for (const [key, expr] of Object.entries(transforms)) {
    // A row added in the builder but never given a target path.
    if (!key.trim()) continue;

    // Split on `|` to get optional separate write/read paths
    const pipeIdx = key.indexOf('|');
    const writeTo = pipeIdx === -1 ? key : key.slice(0, pipeIdx);
    const readFrom = pipeIdx === -1 ? key : key.slice(pipeIdx + 1);

    const rawValue = readPath(clone, readFrom);

    let transformed: string;
    try {
      transformed = String(new Jnx(expr).eval({}, '', { value: rawValue }) ?? rawValue);
    } catch {
      transformed = rawValue;
    }

    writePath(clone, writeTo, transformed);
  }

  return clone;
}

interface CardTrayItemProps {
  rowObject: RowObject;
  markdownTemplate: string;
  htmlTemplate?: string;
  transforms?: Record<string, string>;
  actions: WorkTrayAction[];
  scope: Scope;
  fnBindings: FnBindings;
  testIdBase?: string;
  rowIndex?: number;
}

function CardTrayItem({
  rowObject,
  markdownTemplate,
  htmlTemplate,
  transforms,
  actions,
  scope,
  fnBindings,
  testIdBase,
  rowIndex,
}: CardTrayItemProps): JSX.Element {
  const { i18n } = useLingui();
  const transformedRow = useMemo(() => applyTransforms(rowObject, transforms ?? {}), [rowObject, transforms]);

  const renderedMarkdown = useMemo(() => {
    if (!markdownTemplate) return '';
    return applyTemplate(markdownTemplate, transformedRow as TemplateVariables, i18n.locale, { fallback: '' });
  }, [markdownTemplate, transformedRow, i18n.locale]);

  const renderedHtml = useMemo(() => {
    if (!htmlTemplate) return '';
    return applyTemplate(htmlTemplate, transformedRow as TemplateVariables, i18n.locale, { fallback: '' });
  }, [htmlTemplate, transformedRow, i18n.locale]);

  const rowKey = (rowObject as any)?.id ?? rowIndex;
  const itemBase = testIdBase ? `${testIdBase}.item-${rowKey}` : undefined;

  const content = (
    <>
      {renderedHtml ? (
        <div className="cardtray-item-content">
          <StaticTextBlurb schema={{ content: renderedHtml }} />
        </div>
      ) : renderedMarkdown ? (
        <div className="cardtray-item-content">
          <ReactMarkdown>{renderedMarkdown}</ReactMarkdown>
        </div>
      ) : null}

      {actions.length > 0 ? (
        <div className="cardtray-item-actions">
          <ActionButtonsCell actions={actions} rowObject={rowObject} scope={scope} fnBindings={fnBindings} testIdBase={itemBase} size="xs" />
        </div>
      ) : null}
    </>
  );

  return (
    <Card className="cardtray-item stagger-item" style={{ animationDelay: `${staggerDelay(rowIndex ?? 0)}ms` }} {...getTestId(itemBase)}>
      <CardBody>{content}</CardBody>
    </Card>
  );
}

export default CardTray;
